import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Tag, CheckSquare, Square, Filter, Loader2, FolderTree } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';
const TAG_TYPES = [
  { key: 'actress', label: 'Actrice' },
  { key: 'studio',  label: 'Studio' },
  { key: 'type',    label: 'Type' },
  { key: 'theme',   label: 'Theme' },
];

/*
 * AdminPanel — bulk-beheer voor content (deel 1).
 * Selecteer video's, ken bulk tags/categorie toe. Alleen voor admins.
 * Praat met de tag_routes.js endpoints. Raakt de gewone site niet.
 */
export default function AdminPanel({ onClose, token }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [tagsMap, setTagsMap] = useState({});   // videoId -> [ {id,name,tag_type} ]
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');       // all | untagged | hashtitle
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  // Bulk-invoer
  const [tagInput, setTagInput] = useState('');
  const [tagType, setTagType] = useState('type');
  const [category, setCategory] = useState('');

  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  // --- Video's laden (hergebruikt de bestaande publieke lijst) -------------
  const loadVideos = useCallback(async (p = 1, append = false) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/videos?page=${p}&limit=48`, { headers: authHeaders });
      const data = await res.json();
      const rows = data.videos || [];
      setVideos((prev) => (append ? [...prev, ...rows] : rows));
      setHasMore(rows.length === 48);
      setPage(p);
      // Haal de echte tags (uit video_tags) op voor deze video's
      loadTagsMap(rows.map((v) => v.id));
    } catch (e) {
      showToast('Loading failed');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Haalt de tag-map op voor een set video-id's en voegt toe aan de state.
  const loadTagsMap = async (ids) => {
    if (!ids || ids.length === 0) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/videos/tags-map`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ videoIds: ids }),
      });
      const data = await res.json();
      if (data.map) setTagsMap((prev) => ({ ...prev, ...data.map }));
    } catch (e) { /* stil falen — tags tonen is niet kritiek */ }
  };

  useEffect(() => { loadVideos(1, false); }, [loadVideos]);

  // --- Selectie helpers ----------------------------------------------------
  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const filteredVideos = videos.filter((v) => {
    if (search && !(`${v.title} ${v.actress || ''} ${v.website || ''}`.toLowerCase().includes(search.toLowerCase())))
      return false;
    if (filter === 'untagged' && tagsMap[v.id] && tagsMap[v.id].length > 0) return false;
    if (filter === 'hashtitle' && !/^[0-9a-f]{8,}/i.test(v.title || '')) return false;
    return true;
  });

  const allVisibleSelected = filteredVideos.length > 0 && filteredVideos.every((v) => selected.has(v.id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) filteredVideos.forEach((v) => n.delete(v.id));
      else filteredVideos.forEach((v) => n.add(v.id));
      return n;
    });
  };

  // --- Bulk-acties ---------------------------------------------------------
  const applyTags = async (mode) => {
    const ids = [...selected];
    if (ids.length === 0) return showToast('No videos selected');
    if (!tagInput.trim()) return showToast('Geen tag ingevoerd');
    // Meerdere tags op komma gescheiden toegestaan
    const tags = tagInput.split(',').map((t) => t.trim()).filter(Boolean).map((name) => ({ name, type: tagType }));
    const verb = mode === 'add' ? 'toevoegen aan' : 'verwijderen van';
    if (!window.confirm(`${tags.length} tag(s) ${verb} ${ids.length} video('s)?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/videos/bulk-tag`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ videoIds: ids, tags, mode }),
      });
      const data = await res.json();
      if (data.success) { showToast(`Klaar — ${data.affected} koppeling(en) ${mode === 'add' ? 'toegevoegd' : 'verwijderd'}`); setTagInput(''); loadTagsMap(ids); }
      else showToast(data.error || 'Mislukt');
    } catch (e) { showToast('Actie mislukt'); }
    finally { setBusy(false); }
  };

  const applyCategory = async () => {
    const ids = [...selected];
    if (ids.length === 0) return showToast('No videos selected');
    if (!category.trim()) return showToast('Geen categorie ingevoerd');
    if (!window.confirm(`Set category "${category}" on ${ids.length} video(s)?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/videos/bulk-category`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ videoIds: ids, category: category.trim() }),
      });
      const data = await res.json();
      if (data.success) showToast(`Category set on ${data.affected} video(s)`);
      else showToast(data.error || 'Mislukt');
    } catch (e) { showToast('Actie mislukt'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950 text-white flex flex-col">
      {/* Kop */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-zinc-900">
        <div className="flex items-center gap-2">
          <FolderTree className="w-5 h-5 text-red-500" />
          <h2 className="font-bold text-lg">Content beheer</h2>
          <span className="text-sm text-zinc-400 ml-2">{selected.size} selected</span>
        </div>
        <button onClick={onClose} className="p-2 rounded hover:bg-zinc-800" aria-label="Sluiten">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Bulk-actiebalk */}
      <div className="px-5 py-3 border-b border-zinc-800 bg-zinc-900/60 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-zinc-400" />
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="Tag(s), komma-gescheiden"
            className="bg-zinc-800 rounded px-3 py-1.5 text-sm w-56 outline-none focus:ring-1 focus:ring-red-500"
          />
          <select value={tagType} onChange={(e) => setTagType(e.target.value)}
            className="bg-zinc-800 rounded px-2 py-1.5 text-sm outline-none">
            {TAG_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <button disabled={busy} onClick={() => applyTags('add')}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm disabled:opacity-50">
            + Add
          </button>
          <button disabled={busy} onClick={() => applyTags('remove')}
            className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 rounded text-sm disabled:opacity-50">
            − Remove
          </button>
        </div>
        <div className="h-6 w-px bg-zinc-700" />
        <div className="flex items-center gap-2">
          <FolderTree className="w-4 h-4 text-zinc-400" />
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Category"
            className="bg-zinc-800 rounded px-3 py-1.5 text-sm w-40 outline-none focus:ring-1 focus:ring-red-500"
          />
          <button disabled={busy} onClick={applyCategory}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50">
            Instellen
          </button>
        </div>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
      </div>

      {/* Filter/zoek */}
      <div className="px-5 py-2 border-b border-zinc-800 flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <Search className="w-4 h-4 text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, actress, studio…"
            className="bg-transparent text-sm outline-none flex-1"
          />
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Filter className="w-4 h-4 text-zinc-500" />
          {['all', 'untagged', 'hashtitle'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2 py-1 rounded ${filter === f ? 'bg-red-600' : 'bg-zinc-800 hover:bg-zinc-700'}`}>
              {f === 'all' ? 'All' : f === 'untagged' ? 'Untagged' : 'Hash title'}
            </button>
          ))}
        </div>
        <button onClick={toggleAllVisible} className="flex items-center gap-1 text-sm px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700">
          {allVisibleSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
          Alles
        </button>
      </div>

      {/* Video-grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading && videos.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Laden…
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {filteredVideos.map((v) => {
              const sel = selected.has(v.id);
              return (
                <div key={v.id}
                  onClick={() => toggle(v.id)}
                  className={`relative rounded-lg overflow-hidden cursor-pointer border-2 transition ${sel ? 'border-red-500' : 'border-transparent hover:border-zinc-600'}`}>
                  <div className="aspect-video bg-zinc-800">
                    <img src={`${API_URL}/api/videos/${v.id}/thumbnail`} alt="" loading="lazy"
                      className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute top-1 left-1">
                    {sel ? <CheckSquare className="w-5 h-5 text-red-500 bg-black/50 rounded" />
                         : <Square className="w-5 h-5 text-white/70 bg-black/30 rounded" />}
                  </div>
                  <div className="p-1.5">
                    <p className="text-xs truncate">{v.title}</p>
                    <p className="text-[10px] text-zinc-500 mb-1">{v.category}</p>
                    {tagsMap[v.id] && tagsMap[v.id].length > 0 ? (
                      <div className="flex flex-wrap gap-0.5">
                        {tagsMap[v.id].slice(0, 4).map((t) => (
                          <span key={t.id}
                            className="text-[9px] px-1 py-0.5 rounded bg-zinc-700 text-zinc-200"
                            title={t.tag_type}>
                            {t.name}
                          </span>
                        ))}
                        {tagsMap[v.id].length > 4 && (
                          <span className="text-[9px] text-zinc-500">+{tagsMap[v.id].length - 4}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[9px] text-zinc-600">geen tags</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {hasMore && !loading && (
          <div className="flex justify-center mt-4">
            <button onClick={() => loadVideos(page + 1, true)}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-sm">
              Load more
            </button>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-zinc-800 border border-zinc-700 px-4 py-2 rounded-lg text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
